import './actioncard.scss'

import {Background, DOMAttrs} from '@codeurs/front'
import {CalendarIcon, LocationSmallIcon, PlayIcon} from 'styles/inline-icons'
import React, {FunctionComponent, ReactElement, useState} from 'react'

import Dotdotdot from 'react-dotdotdot'
import {ImageType} from 'campagne/types'
import {Theme} from 'views/campagne/theme'
import {Link as UtilLink} from 'util/link'
import {format} from 'date-fns/esm'
import nldate from 'date-fns/esm/locale/nl'
import {parseISO} from 'date-fns'
import {style} from 'util/campagne/style'
import {useTranslation} from 'util/i18n'

export type ActionCardType = {
	image?: ImageType | string
	title: string
	link?: string
	location: string
	has_video?: boolean
	latitude: string
	longitude: string
	category: string
	keywords?: string
	date_start?: string
	date_end?: string
	dates: {
		start: string
		end?: string
	}
}

const styles = style('actioncard', [
	'infoitem',
	'fullimage',
	'gradient',
	'background'
])

export const ActionCard: FunctionComponent<ActionCardType> = ({
	image,
	title,
	link,
	location,
	dates
}) => {
	const date = parseISO(dates.start)
	const t = useTranslation('campagne').action
	const [isLoaded, setLoaded] = useState(false)
	return (
		<div class={styles.is({loaded: isLoaded})}>
			<UtilLink to={link} target={'_self'}>
				<ActionCardBackground
					img={image}
					onload={() => setLoaded(true)}
					class={styles('image').toString()}
				>
					<div class={styles.gradient} />
					<div class={styles('image-info')}>
						{location && (
							<ActionCardInfoItem
								icon={<LocationSmallIcon />}
								text={location}
							/>
						)}
						{dates.start && (
							<ActionCardInfoItem
								icon={<CalendarIcon />}
								text={format(date, 'd MMMM', {
									locale: nldate
								})}
							/>
						)}
					</div>
				</ActionCardBackground>
				<div class={styles('bottom')}>
					<div class={styles('bottom-title')}>
						<Theme.H3>
							<Dotdotdot clamp={2}>{title}</Dotdotdot>
						</Theme.H3>
					</div>
					<div class={styles('bottom-link')}>
						{link && (
							<Theme.Link to={link} target={'_self'} withArrow={true}>
								{t.watch}
							</Theme.Link>
						)}
					</div>
				</div>
			</UtilLink>
		</div>
	)
}

export const ActionCardFullImage: FunctionComponent<
	ActionCardType & {
		mod?: 'square' | 'big'
	}
> = ({image, title, link, location, dates, mod = null, has_video}) => {
	const t = useTranslation('campagne').action
	const [isLoaded, setLoaded] = useState(false)
	const date = parseISO(dates.start)

	return (
		<div class={styles.fullimage.mod(mod).is({loaded: isLoaded})}>
			{has_video && (
				<div class={styles.fullimage('playicon')}>
					<PlayIcon />
				</div>
			)}
			<UtilLink to={link} target={'_self'}>
				<ActionCardBackground
					img={image}
					class={styles.fullimage('image').toString()}
					onload={() => setLoaded(true)}
				>
					<div class={styles.gradient} />
					<div class={styles.fullimage('bottom')}>
						<div class={styles.fullimage('bottom-title')}>
							<Theme.H3>
								<Dotdotdot clamp={2}>{title}</Dotdotdot>
							</Theme.H3>
						</div>
						<div class={styles.fullimage('bottom-info')}>
							{location && (
								<ActionCardInfoItem
									icon={<LocationSmallIcon />}
									text={location}
								/>
							)}
							{dates.start && (
								<ActionCardInfoItem
									icon={<CalendarIcon />}
									text={format(date, 'd MMMM', {
										locale: nldate
									})}
								/>
							)}
						</div>
						<div class={styles.fullimage('bottom-link')}>
							<Theme.Link to={link} target={'_self'} withArrow={true}>
								{t.watch}
							</Theme.Link>
						</div>
					</div>
				</ActionCardBackground>
			</UtilLink>
		</div>
	)
}

export const ActionCardBackground: FunctionComponent<
	DOMAttrs & {
		img: ImageType | string
		class?: string
	}
> = ({children, img, class: className, ...props}) => {
	if (img && typeof img !== 'string' && (img as ImageType).src)
		return (
			<Background img={img} class={className} {...props}>
				{children}
			</Background>
		)
	return (
		<div class={styles.background.mod('no-image').with(className)}>
			{children}
		</div>
	)
}
export const ActionCardGradient = () => <div class={styles.gradient} />
type InfoItemType = {
	icon?: ReactElement
	text: string
}

export const ActionCardInfoItem: FunctionComponent<InfoItemType> = ({
	icon,
	text
}) => (
	<span class={styles.infoitem}>
		{icon}
		<p>{text}</p>
	</span>
)
